// @ts-strict-ignore
import * as React from "react";
import {
  CollapseContainer,
  CollapseHeader,
  CollapseBody,
  CaretIcon
} from "./styles";
import CaretArrow from "./caret-arrow";
import { HeadingV1, ResponsiveBody } from "components/parts/Typography";
import { Colors } from "themes/color-palette";
import { FramerLazyMotion } from "components/site/FramerLazyMotion";
import { IconMinusCircleOutlined, IconPlusCircleOutlined } from "@hubble/icons";

type Props = {
  initialOpenedIndex?: number;
  children: React.ReactNode[];
};

const Accordion = ({ initialOpenedIndex = -1, children }: Props) => {
  const [openedIndex, setOpenIndex] = React.useState(initialOpenedIndex);
  const toggleOpenIndex = (index) => {
    setOpenIndex((prevIndex) => (index === prevIndex ? -1 : index));
  };
  return (
    <React.Fragment>
      {React.Children.map(children, (child, index) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(
            child as React.ReactElement<{
              isOpen: boolean;
              handleToggle: () => void;
              isLast: boolean;
            }>,
            {
              isOpen: openedIndex === index,
              handleToggle: () => toggleOpenIndex(index),
              isLast: index === children.length - 1
            }
          );
        }
      })}
    </React.Fragment>
  );
};

type CollapseProps = {
  id?: string;
  title?: string;
  customTitle?: (isOpen: boolean) => JSX.Element;
  isOpen?: boolean;
  isLast?: boolean;
  boldTitle?: boolean;
  titleAs?: string;
  handleToggle?: () => void;
  isMobileNav?: boolean;
  children: React.ReactNode;
  dark?: boolean;
  isPlusIcon?: boolean;
  isFromDesignComponent?: boolean;
};

const Collapse = ({
  id,
  title,
  customTitle, // allow the header to be customized
  isOpen = false,
  boldTitle = true,
  handleToggle = () => {},
  isLast = false,
  isMobileNav,
  children,
  dark = false,
  isPlusIcon = false,
  isFromDesignComponent = false,
  titleAs = "h6",
  ...props
}: CollapseProps) => {
  React.useEffect(() => {
    if (window?.location?.hash === `#${id}`) handleToggle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <CollapseContainer
      id={id}
      isLast={isLast}
      isMobileNav={isMobileNav}
      aria-expanded={isOpen}
      {...props}
    >
      <FramerLazyMotion>
        <CollapseHeader onClick={handleToggle} data-testid={"collapse-header"}>
          {Boolean(customTitle) ? (
            customTitle(isOpen)
          ) : (
            <React.Fragment>
              {isFromDesignComponent ? (
                <HeadingV1
                  size="smV1"
                  color={dark ? Colors.white : Colors.black}
                  semiBold
                  mb="0px"
                >
                  {title}
                </HeadingV1>
              ) : (
                <ResponsiveBody
                  color={dark ? Colors.white : Colors.black}
                  semiBold={boldTitle}
                  as={titleAs}
                >
                  {title}
                </ResponsiveBody>
              )}

              {title && (
                <React.Fragment>
                  {isPlusIcon ? (
                    <React.Fragment>
                      {isOpen ? (
                        <IconMinusCircleOutlined
                          size="xl"
                          color={dark ? Colors.white : Colors.black}
                        />
                      ) : (
                        <IconPlusCircleOutlined
                          size="xl"
                          color={dark ? Colors.white : Colors.black}
                        />
                      )}
                    </React.Fragment>
                  ) : (
                    <CaretIcon
                      transition={{ duration: 0.2 }}
                      animate={{ rotate: isOpen ? 270 : 90 }}
                      alt=""
                    >
                      <CaretArrow dark={dark} />
                    </CaretIcon>
                  )}
                </React.Fragment>
              )}
            </React.Fragment>
          )}
        </CollapseHeader>
        <div
          css={`
            padding: 12px 0;
          `}
        >
          <CollapseBody dark={dark} isOpen={isOpen}>
            {children}
          </CollapseBody>
        </div>
      </FramerLazyMotion>
    </CollapseContainer>
  );
};

Accordion.Collapse = Collapse;

export default Accordion;
